import React, { useEffect } from 'react';
import moment from 'moment';
import { Label } from '../../../components/Label';
import { Row } from '../../../utilities/constants';
import {
  Card,
  CardContent,
  Divider,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useSnackbar } from 'notistack';

const COUPON_TYPE = Object.freeze({
  USER: 1,
  SERVICE: 2,
  GENERAL: 4,
  CERTIFICATE: 5,
});

const getCouponTypeLabel = (t) => {
  const type = Number(t);
  if (type === COUPON_TYPE.GENERAL) return 'General coupon';
  if (type === COUPON_TYPE.USER) return 'User coupon';
  if (type === COUPON_TYPE.SERVICE) return 'Service coupon';
  if (type === COUPON_TYPE.CERTIFICATE) return 'Certificate coupon';
  return null;
};

const InvoicePreview = (props) => {
  const {
    previewInvoice = [],
    paymentCoupon,
    onChangeCoupon,
    couponErrorMessage,
    onApplyPaymentCoupon,
    total = {},
    currentCerts = null,
  } = props;
  const { enqueueSnackbar } = useSnackbar();

  const getPrice = (amount = 0) => (Number(amount || 0) / 100).toFixed(2);
  const rawCoupons = Array.isArray(total?.coupons)
    ? total.coupons
    : (Array.isArray(total?.Coupons) ? total.Coupons : []);

  const totals = {
    subtotal: total?.subtotal ?? total?.Subtotal ?? 0,
    tax: total?.tax ?? total?.Tax ?? 0,
    coupon: total?.totalDiscount ?? total?.TotalDiscountAmount ?? 0,
    grandTotal: total?.total ?? total?.Total ?? 0,
    coupons: rawCoupons,
    generalCoupon: total?.generalCoupon ?? null,
    otherCoupons: Array.isArray(total?.otherCoupons) && total.otherCoupons.length > 0 ? total.otherCoupons : rawCoupons.filter(c => c?.CouponType != 4),
    generalDiscountTotal: total?.generalDiscountTotal ?? 0,
    otherDiscountTotals: Array.isArray(total?.otherDiscountTotals) ? total.otherDiscountTotals : [],
    isCouponDisabled: total?.isCouponDisabled ?? total?.IsCouponDisabled ?? false,
    trialDays: total?.trialDays ?? 0,
    forceProration: total?.forceProration ?? false,
  };

  const otherDiscountSum = totals.otherDiscountTotals.reduce((s, v) => s + (v || 0), 0);

  // Proration calculations.
  // Prorate when there are trial days OR when the caller explicitly requests it
  // (e.g. adding certificates mid-month — charged for remaining days even with 0 trial days).
  const hasTrialDays = totals.trialDays > 0;
  const shouldProrate = hasTrialDays || totals.forceProration;
  let chargeDate = null;
  let displaySubtotal = totals.subtotal;
  let displayTotal = totals.grandTotal;

  let prorateRatio = 1;
  let displayCoupon = totals.coupon;
  if (shouldProrate) {
    // When trialDays = 0 chargeStartDate is today; when > 0 it is the trial end date
    const chargeStartDate = moment().add(totals.trialDays, 'days');
    // Only show "Package charged after" banner when there is an actual trial delay
    if (hasTrialDays) {
      chargeDate = chargeStartDate.format('MM/DD/YYYY, h:mm A');
    }
    const endOfMonth = moment(chargeStartDate).endOf('month');
    const remainingDays = endOfMonth.diff(chargeStartDate, 'days');
    const remainingHours = endOfMonth.diff(chargeStartDate, 'hours') % 24;
    const totalRemainingDays = remainingDays + (remainingHours / 24);
    const totalDaysInMonth = endOfMonth.daysInMonth();
    prorateRatio = totalRemainingDays / totalDaysInMonth;

    displaySubtotal = Math.round(((totals.subtotal + totals.coupon) / totalDaysInMonth) * totalRemainingDays);
    const backendNetBeforeTax = totals.grandTotal - (totals.tax ?? 0);
    const proratedNet = Math.round(backendNetBeforeTax * prorateRatio);
    const proratedTax = Math.round((totals.tax ?? 0) * prorateRatio);
    displayCoupon = Math.max(0, displaySubtotal - proratedNet);
    displayTotal = Math.max(0, proratedNet + proratedTax);
  }
  const coupons = totals.coupons;

  const parseDescription = (desc) => {
    const match = desc.match(/(\d+\s*×\s*.*?)(\s*\(at\s*.*?\))/i);
    if (match) {
      return {
        packageName: match[1].trim(),
        monthlyRate: match[2].trim(),
      };
    }
    return { packageName: desc, monthlyRate: '' };
  };

  const handleClearGeneralCoupon = async (couponCode) => {
    try {
      if (!couponCode) return;

      const targetCode = String(couponCode).trim().toLowerCase();
      const codes = coupons
        .map(c => String(c?.Coupon || c?.coupon || '').trim())
        .filter(Boolean)
        .filter(c => c.toLowerCase() !== targetCode);

      if (typeof onApplyPaymentCoupon === 'function') {
        await onApplyPaymentCoupon(codes.length > 0 ? false : true, codes.length > 0 ? codes : []);
      }

      if (onChangeCoupon) {
        onChangeCoupon({ target: { value: '' } });
      }
    } catch (error) {
      console.error("Failed to remove coupon:", error);
    }
  };

  const handleApplyCoupon = async () => {
    const code = (paymentCoupon ?? '').trim();
    if (!code) return;

    const base = Array.isArray(coupons) ? coupons : [];
    const existingCodes = base
      .map(c => (c?.Coupon ?? '').trim())
      .filter(Boolean);

    const exists = existingCodes.some(c => c.toLowerCase() === code.toLowerCase());
    if (exists) {
      enqueueSnackbar?.('Coupon already applied.', { variant: 'warning' });
      onChangeCoupon({ target: { value: '' } });
      return;
    }
    const nextCodes = [...existingCodes, code];

    const result = await onApplyPaymentCoupon(false, nextCodes);

    const toLower = s => s.toLowerCase();
    const newCodeRejected = result?.rejectedCodes?.some(r => toLower(r) === toLower(code));

    if (newCodeRejected) {
      enqueueSnackbar?.(result.errorMessage || `Coupon "${code}" is not applicable.`, { variant: 'error' });
    } else {
      const replacedCodes = existingCodes.filter(c =>
        result?.rejectedCodes?.some(r => toLower(r) === toLower(c))
      );
      if (replacedCodes.length > 0) {
        enqueueSnackbar?.(`Coupon "${replacedCodes[0]}" was replaced with "${code}".`, { variant: 'info' });
      }
    }

    onChangeCoupon({ target: { value: '' } });
  };

  useEffect(() => {
    if (totals.isCouponDisabled) {
      enqueueSnackbar?.(
        'Coupon temporarily unavailable. Please wait 10 minutes.',
        { variant: 'warning' }
      );
    }
  }, [totals.isCouponDisabled, enqueueSnackbar]);

  const generalCoupon = coupons.find(c => c?.CouponType == 4);
  const generalCouponTypeLabel = getCouponTypeLabel(generalCoupon?.CouponType);

  return (
    <Row style={{ background: 'transparent', border: 'none', boxShadow: 'none' }}>
      <div className="w-full max-w-[752px]">
        {/* ORDER SUMMARY CARD */}
        <Card
          elevation={0}
          style={{
            border: '1px solid #A2A2A2',
            borderRadius: 16,
          }}
        >
          <CardContent style={{ padding: 16 }}>
            {/* Title */}
            <Typography style={{ fontWeight: 700, fontSize: 18, display: 'flex' }}>
              Order Summary
            </Typography>

            <Divider style={{ marginTop: 12, marginBottom: 12 }} />

            {/* Product List */}
            <Box>
              {previewInvoice.length === 0 ? (
                <Typography style={{ color: '#6B7280', fontSize: 14 }}>
                  No items added yet.
                </Typography>
              ) : (
                previewInvoice.map((invoice, idx) => {
                  const { packageName, monthlyRate } = parseDescription(invoice.description || invoice.Description || '');

                  const lineNet = invoice.amount;

                  const otherCoupons = Array.isArray(totals.otherCoupons) && totals.otherCoupons.length > 0
                    ? totals.otherCoupons
                    : coupons.filter(c => c?.CouponType != 4);

                  const otherDiscounts = Array.isArray(invoice.otherDiscounts) && invoice.otherDiscounts.length > 0
                    ? invoice.otherDiscounts
                    : (Array.isArray(invoice.DiscountAmounts) ? invoice.DiscountAmounts.map(d => d.DiscountAmount || d.discountAmount) : []);

                  const hasOther = otherCoupons.length > 0;

                  return (
                    <Box key={idx}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography
                          style={{
                            fontWeight: 700,
                            fontSize: 16,
                            color: '#6A0DAD',
                          }}
                        >
                          {packageName}
                        </Typography>

                        <Typography style={{ fontWeight: 700 }}>
                          ${getPrice(lineNet)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="flex-start">
                        <Typography style={{ fontSize: 14, color: '#6B7280', marginTop: 4 }}>
                          {`${monthlyRate}`}
                        </Typography>
                      </Box>

                      <Box mb={3} mt={6}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          style={{
                            border: '1px solid #A2A2A2',
                            borderRadius: 16,
                            padding: 12,
                            background: '#FAFAFA',
                          }}
                        >
                          {hasOther ? (
                            <>
                              <Typography style={{ fontWeight: 600, marginBottom: 8 }}>
                                Coupons Applied
                              </Typography>

                              {otherCoupons.map((c, i) => {
                                const d = Math.round((otherDiscounts[i] || 0) * prorateRatio);

                                return (
                                  <Box
                                    key={`${c?.Coupon ?? 'coupon'}-${i}`}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    style={{ marginTop: i ? 6 : 0 }}
                                  >
                                    <Typography style={{ fontSize: 14 }}>
                                      {getCouponTypeLabel(c?.CouponType)}
                                      {c?.Coupon ? ` (${c.Coupon})` : ''}
                                    </Typography>

                                    <Box display="flex" alignItems="center">
                                      <Typography style={{ fontWeight: 700 }}>
                                        -${getPrice(d)}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        aria-label="remove coupon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleClearGeneralCoupon(c?.Coupon || c?.coupon);
                                        }}
                                        style={{ marginLeft: 8 }}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </>
                          ) : (
                            <Typography style={{ color: '#9CA3AF', fontWeight: 500 }}>
                              No coupon applied
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Divider style={{ marginTop: 12, marginBottom: 12 }} />
                    </Box>
                  );
                })
              )}
            </Box>

            {/* GENERAL COUPON APPLIED */}
            <Box mb={3} mt={6}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{
                  border: '1px solid #A2A2A2',
                  borderRadius: 16,
                  padding: 12,
                  background: '#FAFAFA',
                }}
              >
                {generalCoupon ? (
                  <>
                    <Typography style={{ fontWeight: 600 }}>
                      {generalCouponTypeLabel} Applied{generalCoupon.Coupon ? ` (${generalCoupon.Coupon})` : ''}
                    </Typography>

                    <Box display="flex" alignItems="center">
                      <Typography style={{ fontWeight: 700 }}>
                        -${getPrice(Math.round((totals.generalDiscountTotal || totals.coupon) * prorateRatio))}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="remove coupon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearGeneralCoupon(generalCoupon?.Coupon || generalCoupon?.coupon);
                        }}
                        style={{ marginLeft: 8 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </>
                ) : (
                  <Typography style={{ color: '#9CA3AF', fontWeight: 500 }}>
                    No General Coupon applied
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Totals */}
            <Box>
              <Box display="flex" justifyContent="space-between" style={{ marginTop: 4 }}>
                <Typography style={{ fontWeight: 600 }}>
                  {'Subtotal'}
                </Typography>
                <Typography style={{ fontWeight: 600 }}>${getPrice(displaySubtotal)}</Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" style={{ marginTop: 6 }}>
                <Typography style={{ fontWeight: 600 }}>Tax</Typography>
                <Typography style={{ fontWeight: 600 }}>${getPrice(totals.tax)}</Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" style={{ marginTop: 6 }}>
                <Typography style={{ fontWeight: 600 }}>
                  {totals.couponName ? `Coupon (${totals.couponName})` : 'Coupon'}
                </Typography>
                <Typography style={{ fontWeight: 600 }}>
                  -${getPrice(displayCoupon)}
                </Typography>
              </Box>

              {currentCerts !== null && (
                <Box display="flex" justifyContent="space-between" style={{ marginTop: 6 }}>
                  <Typography style={{ fontWeight: 600 }}>Current Certificates</Typography>
                  <Typography style={{ fontWeight: 600 }}>{currentCerts}</Typography>
                </Box>
              )}
              {/* Trial days — package charge date */}
              {hasTrialDays && (
                <Box mb={3} mt={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    style={{
                      border: '1px solid #10B981',
                      borderRadius: 16,
                      padding: 12,
                      background: '#F0FDF4',
                    }}
                  >
                    <Typography style={{ fontWeight: 600, color: '#065F46' }}>
                      Trial End Date
                    </Typography>
                    <Typography style={{ fontWeight: 700, color: '#065F46' }}>
                      {chargeDate}
                    </Typography>
                  </Box>
                </Box>
              )}


              <Divider style={{ marginTop: 12, marginBottom: 12 }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography style={{ fontWeight: 800, fontSize: 16 }}>Total</Typography>
                <Typography style={{ fontWeight: 800, fontSize: 16 }}>
                  ${getPrice(displayTotal)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* COUPON INPUT */}
        <Box style={{ marginTop: 16 }}>
          <label className="block text-[#111827] text-[16px] font-[700] text-start">
            Coupon
          </label>

          <Box
            display="flex"
            alignItems="flex-start"
            gridGap={12}
            style={{ marginTop: 8 }}
          >
            <TextField
              name="paymentCoupon"
              placeholder="Enter coupon"
              value={paymentCoupon}
              onChange={onChangeCoupon}
              error={!!couponErrorMessage}
              helperText={couponErrorMessage || ' '}
              size="small"
              variant="outlined"
              InputProps={{
                style: { borderRadius: 8, borderColor: '#666' }
              }}
              FormHelperTextProps={{
                style: { minHeight: 20 }
              }}
              margin="dense"
              fullWidth
              disabled={totals.grandTotal <= 0 || totals.isCouponDisabled}
            />

            <Button
              variant="contained"
              disabled={totals.grandTotal <= 0 || paymentCoupon === '' || totals.isCouponDisabled}
              onClick={handleApplyCoupon}
              style={{
                height: 40,
                padding: '0 20px',
                borderRadius: 8,
                backgroundColor: (totals.grandTotal <= 0 || paymentCoupon === '' || totals.isCouponDisabled) ? 'rgba(16,185,129,0.4)' : '#10B981',
                color: '#fff',
                textTransform: 'none',
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: 14,
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                marginTop: 6,
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </div>
    </Row>
  );
};

export default InvoicePreview;